import { useCallback, useEffect, useMemo, useState } from 'react'
import { showNotification } from '@mantine/notifications'
import { api, ApiError } from '@/lib/api'
import {
  draftToAiInstructionForm,
  emptyAiInstructionForm,
  type AiInstructionFormState,
} from '@/components/tickets/ticket-detail-page-helpers'
import type {
  AIInstructionSimilarRow,
  AIInstructionWritePayload,
  TicketDetailTicket,
} from '@/types/api'

type UseTicketAiInstructionParams = {
  ticket: TicketDetailTicket | null
  ticketId: string | null
  userRole?: string | null
}

function buildAiInstructionPayload(form: AiInstructionFormState): AIInstructionWritePayload {
  return {
    category: form.category.trim(),
    triggers: form.triggers.trim(),
    action: form.action.trim(),
    response_from_db: form.response_from_db.trim(),
    data_collected: form.data_collected.trim(),
    questions_asked: form.questions_asked.trim(),
    tags: form.tags.trim(),
    priority: form.priority,
    when_transfer_to_operator: form.when_transfer_to_operator.trim(),
    message_to_client_on_transfer: form.message_to_client_on_transfer.trim(),
    is_active: true,
  }
}

export function useTicketAiInstruction({
  ticket,
  ticketId,
  userRole,
}: UseTicketAiInstructionParams) {
  const [showCreateInstructionBtn, setShowCreateInstructionBtn] = useState(false)
  const [opened, setOpened] = useState(false)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [meta, setMeta] = useState('')
  const [form, setForm] = useState<AiInstructionFormState>(emptyAiInstructionForm)
  const [similar, setSimilar] = useState<AIInstructionSimilarRow | null>(null)
  const [similarReason, setSimilarReason] = useState('')

  const canManageInstructionsByRole = useMemo(
    () => ['owner', 'admin', 'analyst'].includes(String(userRole || '').toLowerCase()),
    [userRole],
  )

  useEffect(() => {
    if (!canManageInstructionsByRole || !ticket?.project_id) {
      setShowCreateInstructionBtn(false)
      return
    }
    let cancelled = false
    void api
      .projectAICapabilities(ticket.project_id)
      .then((settings) => {
        if (cancelled) return
        const provider = String(settings?.provider_type || '').toLowerCase()
        setShowCreateInstructionBtn(
          Boolean(settings?.is_enabled) && (provider === 'openai' || provider === 'vllm'),
        )
      })
      .catch(() => {
        if (!cancelled) setShowCreateInstructionBtn(false)
      })
    return () => {
      cancelled = true
    }
  }, [canManageInstructionsByRole, ticket?.project_id])

  const onSummary = useCallback(async () => {
    if (!ticketId) return
    try {
      await api.generateConversationSummary(ticketId)
      showNotification({ title: 'Саммари', message: 'Внутренняя заметка с саммари создана.', color: 'green' })
    } catch (requestError) {
      showNotification({
        title: 'Ошибка',
        message: requestError instanceof ApiError ? requestError.message : 'Не удалось запросить саммари',
        color: 'red',
      })
    }
  }, [ticketId])

  const openAiInstructionModal = useCallback(() => {
    const projectId = ticket?.project_id
    const sourceTicketId = ticket?.id
    if (!projectId || !sourceTicketId) return
    setOpened(true)
    setLoading(true)
    setError(null)
    setMeta('')
    setSimilar(null)
    setSimilarReason('')
    setForm(emptyAiInstructionForm())
    void (async () => {
      try {
        const data = await api.suggestAIInstructionFromTicket(projectId, sourceTicketId)
        const draft = data.generated_instruction
        setForm(draftToAiInstructionForm(draft ?? undefined))
        setSimilar(data.similar_instruction?.id ? data.similar_instruction : null)
        setSimilarReason(data.similarity_reason || '')
        const providerUsed = data.provider_used ? String(data.provider_used).toUpperCase() : 'AI'
        const confidence = Number.isFinite(Number(data.confidence))
          ? Math.round(Number(data.confidence) * 100)
          : null
        setMeta(
          `Провайдер: ${providerUsed}${confidence != null ? ` • Уверенность: ${confidence}%` : ''}`,
        )
      } catch (requestError) {
        setError(requestError instanceof ApiError ? requestError.message : String(requestError))
      } finally {
        setLoading(false)
      }
    })()
  }, [ticket?.id, ticket?.project_id])

  const saveAiInstruction = useCallback(
    async (mode: 'create' | 'replace') => {
      const projectId = ticket?.project_id
      if (!projectId) return
      const payload = buildAiInstructionPayload(form)
      if (!payload.category || !payload.triggers || !payload.action) {
        showNotification({
          title: 'Инструкция',
          message: 'Заполните минимум: категория, триггеры и действие',
          color: 'red',
        })
        return
      }
      setSaving(true)
      try {
        if (mode === 'replace') {
          if (!similar?.id) {
            showNotification({ title: 'Инструкция', message: 'Похожая инструкция не найдена', color: 'red' })
            return
          }
          await api.updateAIInstruction(projectId, similar.id, payload)
          showNotification({
            title: 'Готово',
            message: 'Инструкция заменена. Чтобы она заработала, пересоздайте ассистента.',
            color: 'green',
          })
        } else {
          await api.createAIInstruction(projectId, payload)
          showNotification({
            title: 'Готово',
            message: 'Инструкция добавлена. Чтобы она заработала, пересоздайте ассистента.',
            color: 'green',
          })
        }
        setOpened(false)
      } catch (saveError) {
        showNotification({
          title: 'Ошибка',
          message: saveError instanceof ApiError ? saveError.message : 'Ошибка сохранения инструкции',
          color: 'red',
        })
      } finally {
        setSaving(false)
      }
    },
    [form, similar?.id, ticket?.project_id],
  )

  return {
    aiInstructionModal: {
      opened,
      loading,
      saving,
      error,
      meta,
      form,
      similar,
      similarReason,
      setForm,
      onClose: () => setOpened(false),
      onSave: saveAiInstruction,
    },
    onSummary,
    openAiInstructionModal,
    showCreateInstructionBtn,
  }
}
